import { Inject, Injectable } from '@angular/core';
import { AUTOMATION_MARKET_ID_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { periodDays } from '../rate-filter';

@Injectable({ providedIn: 'root' })
export class ActivateLimitedProductsService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    protected readonly appv2Service: AppPartnerService,
    @Inject(AUTOMATION_MARKET_ID_INJECTION_TOKEN$)
    private readonly marketId$: Observable<string>,
  ) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.PREPROCESSOR.PRODUCT_ERROR'),
        this.emptyBody(),
      );
    }

    const nextStepId = activity?.nextWorkflowStep?.id;
    const executedStep = activity?.workflowStep;
    let displayContent = 'AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.PREPROCESSOR.RATE_ERROR';

    if (executedStep.choices?.defaultNextStep === nextStepId) {
      displayContent = 'AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.PREPROCESSOR.SUCCESS';
    }

    // Show user-friendly text if the step proceeded.
    return this.tableEntry(this.translateService.stream(displayContent), this.emptyBody());
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    if (data.line_items.length > 0) {
      const firstLineItem = data.line_items.length > 0 ? data.line_items[0] : null;
      const appId = firstLineItem.identifier?.app_key?.app_id || '';
      const editionId = firstLineItem.identifier?.app_key?.edition_id;

      const apps$ = this.marketId$.pipe(
        switchMap((marketId) => {
          return this.appv2Service.getMulti(
            [{ appId: appId, editionId: editionId }] as AppKey[],
            namespace,
            marketId,
            null,
            true,
          );
        }),
      );

      return apps$.pipe(
        take(1),
        map((apps) => {
          if (apps.length > 0) {
            let productName = apps[0]?.sharedMarketingInformation?.name;
            if (apps[0]?.sharedMarketingInformation?.editionName) {
              productName += ' | ' + apps[0]?.sharedMarketingInformation?.editionName;
            }
            let plusOthers = '';
            if (data.line_items.length > 1) {
              const otherCount = data.line_items.length - 1;
              const otherKey =
                otherCount === 1 ? 'AUTOMATIONS.EDITOR.FILTERS.OTHER' : 'AUTOMATIONS.EDITOR.FILTERS.OTHERS';
              const other = this.translateService.instant(otherKey);
              plusOthers = ` (+${otherCount} ${other})`;
            }
            let period =
              +data.length > 1
                ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.HOURS')
                : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.HOUR');
            if (data.period === periodDays) {
              period =
                +data.length > 1
                  ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.DAYS')
                  : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.DAY');
            }
            const time =
              +data.frequency > 1
                ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.TIMES')
                : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.TIME');
            return (
              this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTION', {
                productName: productName,
                plusOthers: plusOthers,
              }) +
              this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.PREPROCESSOR.SUB_TITLE', {
                frequency: data.frequency,
                length: data.length,
                period: period,
                time: time,
              })
            );
          }
        }),
        switchMap((s) => {
          return this.nodeDisplayData(this.translateService.stream(s), this.noValidationErrorsDetected());
        }),
      );
    }
  }
}
